import type {
  ChargeItem,
  Encounter,
  Invoice,
  Resource,
} from '@medplum/fhirtypes';
import {
  BILLING_ADJUSTMENT_EXTENSION,
  BILLING_CLIENT_PAID_EXTENSION,
  BILLING_CLIENT_RESPONSIBILITY_EXTENSION,
  BILLING_EXTERNAL_CANDID_CLAIM_ID_EXTENSION,
  BILLING_INSURANCE_PAID_EXTENSION,
  BILLING_INSURANCE_RESPONSIBILITY_EXTENSION,
  BILLING_PAYMENT_STATUS_EXTENSION,
  BILLING_WRITE_OFF_EXTENSION,
  CPT_CODING_SYSTEM,
} from '@unify/fhir-model/constants';
import {
  chargeItemToServiceLine,
  extractMoneyFromExtension,
  extractStringFromExtension,
  safeReferenceId,
} from '@/pages/client-portal/utils/portalBillingFhirAdapters';
import { billingStatusLabel } from '@/pages/client-portal/utils/portalBillingModels';
import { toClientClaimNumber } from './billingTimelineFilter';
import type { BillingTimelineItem, BillingServiceType } from './billingTimelineTypes';

function encounterServiceType(enc?: Encounter): BillingServiceType {
  if (!enc) return 'other';
  const typeText = enc.type?.[0]?.text?.toLowerCase() ?? '';
  if (typeText.includes('group')) return 'group';
  if (typeText.includes('assessment')) return 'assessment';
  if ((enc.appointment?.length ?? 0) > 0) return 'appointment';
  return 'other';
}

function serviceHref(enc?: Encounter, apptId?: string): string | undefined {
  if (apptId) return `/dashboard/appointments/${apptId}`;
  if (enc?.id) return `/dashboard/clients/chart?encounter=${enc.id}`;
  return undefined;
}

function claimStatusLabel(raw?: string): string {
  if (!raw) return 'Unknown';
  const lower = raw.toLowerCase();
  if (lower.includes('paid') || lower === 'balanced') return 'Paid';
  if (lower.includes('denied') || lower.includes('rejected')) return 'Denied';
  if (lower.includes('pending') || lower.includes('submitted')) return 'Pending';
  if (lower.includes('processing')) return 'Processing';
  return raw;
}

function toClientVisibleStatus(adminStatus?: string, claimStatus?: string): string {
  if (claimStatus) return claimStatusLabel(claimStatus);
  return claimStatusLabel(adminStatus);
}

export interface BuildBillingTimelineInput {
  chargeItems: ChargeItem[];
  encounters: Encounter[];
  invoices: Invoice[];
  encounterById?: Map<string, Encounter>;
}

export function buildBillingTimeline(input: BuildBillingTimelineInput): BillingTimelineItem[] {
  const encounterById = input.encounterById ?? new Map<string, Encounter>();
  for (const enc of input.encounters) {
    if (enc.id) encounterById.set(enc.id, enc);
  }

  const items: BillingTimelineItem[] = [];

  for (const ci of input.chargeItems) {
    const encId = safeReferenceId(ci.context?.reference, 'Encounter');
    const enc = encId ? encounterById.get(encId) : undefined;
    const line = chargeItemToServiceLine(ci, enc);
    const res = ci as Resource;
    const claimId = extractStringFromExtension(res, BILLING_EXTERNAL_CANDID_CLAIM_ID_EXTENSION);
    const adminStatus = extractStringFromExtension(res, BILLING_PAYMENT_STATUS_EXTENSION) ?? ci.status;
    const insuranceBilled = extractMoneyFromExtension(res, BILLING_INSURANCE_RESPONSIBILITY_EXTENSION);
    const insurancePaid = extractMoneyFromExtension(res, BILLING_INSURANCE_PAID_EXTENSION);
    const writeOff = extractMoneyFromExtension(res, BILLING_WRITE_OFF_EXTENSION);
    const adjustment = extractMoneyFromExtension(res, BILLING_ADJUSTMENT_EXTENSION);
    const apptId = enc?.appointment?.[0]?.reference
      ? safeReferenceId(enc.appointment[0].reference, 'Appointment')
      : undefined;

    const claimStatus = adminStatus;
    items.push({
      id: `charge-${ci.id ?? line.id}`,
      dateOfService: line.dateOfService,
      type: 'service',
      serviceType: encounterServiceType(enc),
      sourceReference: encId ? { reference: `Encounter/${encId}` } : ci.context,
      claimReference: claimId ? { reference: `Claim/${claimId}` } : undefined,
      clientClaimNumber: toClientClaimNumber(claimId),
      cptCode: line.cptCode,
      providerName: line.provider,
      totalFee: line.feeCents,
      clientResponsibility: line.clientResponsibilityCents,
      clientPaid: line.clientPaidCents,
      insuranceBilled,
      insurancePaid,
      insuranceBalance:
        insuranceBilled != null && insurancePaid != null
          ? Math.max(0, insuranceBilled - insurancePaid)
          : undefined,
      writeOff: (writeOff ?? 0) + (adjustment ?? 0),
      claimStatus,
      clientVisibleStatus: toClientVisibleStatus(adminStatus, claimStatus),
      adminStatusDetails: adminStatus,
      serviceLabel: line.serviceType,
      sourceHref: serviceHref(enc, apptId),
    });
  }

  for (const enc of input.encounters) {
    if (items.some((i) => i.sourceReference?.reference === `Encounter/${enc.id}`)) continue;
    const apptId = enc.appointment?.[0]?.reference
      ? safeReferenceId(enc.appointment[0].reference, 'Appointment')
      : undefined;
    const cpt = enc.type?.[0]?.coding?.find((c) => c.system === CPT_CODING_SYSTEM)?.code;
    items.push({
      id: `enc-${enc.id}`,
      dateOfService: enc.period?.start,
      type: 'service',
      serviceType: encounterServiceType(enc),
      sourceReference: enc.id ? { reference: `Encounter/${enc.id}` } : undefined,
      cptCode: cpt,
      providerName: enc.participant?.[0]?.individual?.display,
      clientVisibleStatus: enc.status === 'finished' ? 'Completed' : 'Scheduled',
      adminStatusDetails: enc.status,
      serviceLabel: enc.type?.[0]?.text ?? 'Session',
      sourceHref: serviceHref(enc, apptId),
    });
  }

  for (const inv of input.invoices) {
    const res = inv as Resource;
    const clientResp = extractMoneyFromExtension(res, BILLING_CLIENT_RESPONSIBILITY_EXTENSION);
    const clientPaid = extractMoneyFromExtension(res, BILLING_CLIENT_PAID_EXTENSION) ?? 0;
    const adminStatus = extractStringFromExtension(res, BILLING_PAYMENT_STATUS_EXTENSION) ?? inv.status;
    items.push({
      id: `inv-${inv.id}`,
      dateOfService: inv.date,
      transactionDate: inv.date,
      type: 'payment',
      totalFee: clientResp,
      clientResponsibility: clientResp,
      clientPaid,
      clientVisibleStatus: billingStatusLabel(
        adminStatus === 'paid' || inv.status === 'balanced' ? 'paid' : 'due',
      ),
      adminStatusDetails: adminStatus,
      serviceLabel: inv.type?.text ?? 'Invoice',
      paymentReference: inv.id ? { reference: `Invoice/${inv.id}` } : undefined,
    });
  }

  return items.sort((a, b) => {
    const da = new Date(a.dateOfService ?? a.transactionDate ?? 0).getTime();
    const db = new Date(b.dateOfService ?? b.transactionDate ?? 0).getTime();
    return db - da;
  });
}
